import type { Account, Category, CreditCard } from '@domain/finance'
import { formatBRL } from '@shared/money/format'
import { centsToInput, parseAmountInput } from '@shared/money/parse'
import { NO_FILTERS, activeCount } from './filters'
import type { PaymentMethod, TransactionFilters } from './filters'

/**
 * A busca, os filtros e os chips removíveis.
 *
 * `Docs/04-Design/Telas/Transações.md`: *"Categoria (com descendentes), conta ou
 * cartão, método, faixa de valor, e 'só não categorizadas'… Tudo na URL. Filtro
 * ativo aparece como chip removível abaixo do cabeçalho."*
 *
 * ## Este componente não guarda estado
 *
 * Toda mudança vira `onChange(novosFiltros)`, e quem navega é a rota. Um estado
 * local aqui daria duas fontes de verdade para a mesma pergunta — e a que
 * perde é sempre a URL, que é justamente a que a nota escolheu. É também o que
 * faz o "voltar" do navegador desfazer um filtro.
 *
 * ## Os selects ficam atrás de um `<details>`
 *
 * A busca fica sempre visível; o resto abre. São cinco controles para uma tela
 * cujo trabalho principal é ler a lista, e cinco selects permanentes empurrariam
 * a primeira transação para fora da dobra no telefone. O `<details>` é nativo:
 * teclado, leitor de tela e o estado de aberto/fechado saem de graça.
 *
 * ## A faixa de valor é digitada em REAIS e viaja em centavos
 *
 * `parseAmountInput` e `centsToInput` são os mesmos do cadastro — a conversão
 * mora num lugar só. Um campo vazio é `null`, e não zero: zero é uma faixa
 * legítima ("até R$ 0,00" não devolve nada, mas é o que a pessoa pediu).
 */

const METHOD_LABELS: Record<PaymentMethod, string> = {
  pix: 'Pix',
  debit: 'Débito',
  credit: 'Crédito',
  boleto: 'Boleto',
  transfer: 'Transferência',
  cash: 'Dinheiro',
  other: 'Outro',
}

/**
 * **A tabela acima é de runtime.** Um método que exista no tipo e falte aqui
 * some do seletor em silêncio — e um filtro que não se pode escolher é um
 * filtro que não existe. A linha abaixo só compila se ela cobrir a união.
 */
const METHODS = Object.keys(METHOD_LABELS) as PaymentMethod[]

type Props = {
  filters: TransactionFilters
  onChange: (filters: TransactionFilters) => void
  categories: Category[]
  accounts: Account[]
  creditCards: CreditCard[]
}

/** O rótulo de cada chip ativo, e o filtro que o remover produz. */
type Chip = { key: string; label: string; without: Partial<TransactionFilters> }

function chipsOf(
  filters: TransactionFilters,
  names: { categories: Map<string, string>; instruments: Map<string, string> },
): Chip[] {
  const chips: Chip[] = []
  const q = filters.q.trim()
  if (q !== '') chips.push({ key: 'q', label: `"${q}"`, without: { q: '' } })
  if (filters.categoryId !== null) {
    chips.push({
      key: 'categoria',
      // Categoria arquivada continua resolvendo, e uma apagada não resolve —
      // dizer o id cru seria pior que dizer que ela sumiu.
      label: names.categories.get(filters.categoryId) ?? 'categoria que não existe mais',
      without: { categoryId: null },
    })
  }
  if (filters.instrumentId !== null) {
    chips.push({
      key: 'instrumento',
      label: names.instruments.get(filters.instrumentId) ?? 'conta que não existe mais',
      without: { instrumentId: null },
    })
  }
  if (filters.method !== null) {
    chips.push({
      key: 'metodo',
      label: METHOD_LABELS[filters.method],
      without: { method: null },
    })
  }
  if (filters.minCents !== null) {
    chips.push({
      key: 'min',
      label: `a partir de ${formatBRL(filters.minCents)}`,
      without: { minCents: null },
    })
  }
  if (filters.maxCents !== null) {
    chips.push({
      key: 'max',
      label: `até ${formatBRL(filters.maxCents)}`,
      without: { maxCents: null },
    })
  }
  if (filters.onlyUncategorized) {
    chips.push({
      key: 'sem_categoria',
      label: 'sem categoria',
      without: { onlyUncategorized: false },
    })
  }
  return chips
}

/**
 * Reais digitados → centavos. Campo vazio é `null` — "sem limite" —, e não 0.
 * Texto que não é dinheiro **mantém o filtro anterior** em vez de virar `null`:
 * apagar um filtro porque alguém digitou uma letra seria uma surpresa.
 */
function centsFromInput(raw: string, previous: number | null): number | null {
  const parsed = parseAmountInput(raw)
  if (parsed.status === 'empty') return null
  // Texto que não é dinheiro mantém o filtro anterior. E o **módulo**: a faixa
  // compara grandeza (ver `filters.ts`), e um "-50" digitado quer dizer
  // cinquenta reais, não menos cinquenta.
  return parsed.status === 'ok' ? Math.abs(parsed.cents) : previous
}

export function FilterBar({ filters, onChange, categories, accounts, creditCards }: Props) {
  const categoryNames = new Map(categories.map((c) => [c.id, c.name]))
  const instrumentNames = new Map([
    ...accounts.map((a) => [a.id, a.name] as const),
    ...creditCards.map((c) => [c.id, c.name] as const),
  ])
  const chips = chipsOf(filters, {
    categories: categoryNames,
    instruments: instrumentNames,
  })
  const ligados = activeCount(filters)

  const set = (patch: Partial<TransactionFilters>) => {
    onChange({ ...filters, ...patch })
  }

  return (
    <div className="mb-4">
      <label className="block">
        <span className="sr-only">Buscar por descrição</span>
        <input
          type="search"
          data-atalho-busca
          value={filters.q}
          placeholder="Buscar por descrição"
          className="w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-sm"
          onChange={(event) => {
            set({ q: event.target.value })
          }}
        />
      </label>

      <details className="mt-2">
        <summary className="cursor-pointer text-sm text-[var(--text-muted)]">
          {ligados === 0 ? 'Filtros' : `Filtros · ${String(ligados)}`}
        </summary>

        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <label className="text-sm">
            <span className="block text-xs text-[var(--text-muted)]">Categoria</span>
            <select
              value={filters.categoryId ?? ''}
              className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
              onChange={(event) => {
                set({ categoryId: event.target.value === '' ? null : event.target.value })
              }}
            >
              <option value="">Todas</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {/* Indenta pela profundidade: a árvore tem três níveis, e uma
                      lista plana de "Feira" e "Mercado" não diz de quem é
                      qual. */}
                  {`${'  '.repeat(category.depth)}${category.name}`}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="block text-xs text-[var(--text-muted)]">Conta ou cartão</span>
            <select
              value={filters.instrumentId ?? ''}
              className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
              onChange={(event) => {
                set({ instrumentId: event.target.value === '' ? null : event.target.value })
              }}
            >
              <option value="">Todos</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
              {creditCards.map((card) => (
                <option key={card.id} value={card.id}>
                  {card.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="block text-xs text-[var(--text-muted)]">Método</span>
            <select
              value={filters.method ?? ''}
              className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
              onChange={(event) => {
                const value = event.target.value
                set({ method: value === '' ? null : (value as PaymentMethod) })
              }}
            >
              <option value="">Todos</option>
              {METHODS.map((method) => (
                <option key={method} value={method}>
                  {METHOD_LABELS[method]}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="text-sm">
            <legend className="text-xs text-[var(--text-muted)]">Faixa de valor</legend>
            <div className="flex gap-2">
              <label className="flex-1">
                <span className="sr-only">Valor mínimo</span>
                <input
                  inputMode="decimal"
                  placeholder="mínimo"
                  defaultValue={filters.minCents === null ? '' : centsToInput(filters.minCents)}
                  className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
                  onBlur={(event) => {
                    set({ minCents: centsFromInput(event.target.value, filters.minCents) })
                  }}
                />
              </label>
              <label className="flex-1">
                <span className="sr-only">Valor máximo</span>
                <input
                  inputMode="decimal"
                  placeholder="máximo"
                  defaultValue={filters.maxCents === null ? '' : centsToInput(filters.maxCents)}
                  className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
                  onBlur={(event) => {
                    set({ maxCents: centsFromInput(event.target.value, filters.maxCents) })
                  }}
                />
              </label>
            </div>
          </fieldset>
        </div>
      </details>

      {chips.length > 0 && (
        <ul aria-label="Filtros ativos" className="mt-2 flex flex-wrap gap-2 text-xs">
          {chips.map((chip) => (
            <li key={chip.key}>
              <button
                type="button"
                className="rounded-full border border-[var(--border)] alvo px-2 py-1"
                onClick={() => {
                  set(chip.without)
                }}
              >
                {`${chip.label} ×`}
              </button>
            </li>
          ))}
          {chips.length > 1 && (
            <li>
              <button
                type="button"
                className="alvo justify-start underline"
                onClick={() => {
                  onChange(NO_FILTERS)
                }}
              >
                Limpar filtros
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
